import { createStyles, Drawer, DrawerProps, Loader, useMantineTheme } from "@mantine/core";
import { isGraphQLError } from "@rpi/openapi-api";
import { Flex, IconV2, RpiText, RpiTitle } from "@rpi/openapi-core";
import React from "react";
import { useDevMode } from "../../hooks/useDevMode";
import { optObj } from "../../utils/misc.util";
import { RpaButtonLink } from "../../components/RpiLink";
import { IconV3 } from "@rpi/openapi-core";

export interface RpaDrawerProps extends Omit<DrawerProps, "classNames" | "id"> {
  isLoading?: boolean;
  error?: any;
  overlay?: Omit<RpaDrawerOverlayProps, "overlay">;
}

const useRpaDrawerStyles = createStyles((theme) => {
  return {
    overlay: {
      opacity: `1 !important`,
      backgroundColor: "rgba(92, 92, 92, 0.67) !important",
    },
    drawer: {
      width: 362,
      padding: "0px !important",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      alignItems: "flex-start",
      width: "100%",
      padding: `${theme.spacing.lg}px ${theme.spacing.lg}px !important`,
      margin: 0,
    },
    title: {
      marginRight: 0,
      width: "100%",
    },
  };
});

export const RpaDrawer: React.FC<RpaDrawerProps> = ({
  title,
  onClose,
  isLoading,
  error,
  overlay,
  children,
  ...props
}) => {
  const devMode = useDevMode();
  const { classes } = useRpaDrawerStyles();
  const theme = useMantineTheme();

  const childrenRender = React.useMemo(() => {
    if (overlay) return;

    if (error) {
      return (
        <RpaDrawerOverlay
          overlay={false}
          type="error"
          message={isGraphQLError(error) ? error.message : "Failed to load."}
        />
      );
    }

    if (isLoading) {
      return <RpaDrawerOverlay overlay={false} type="loading" />;
    }

    if (devMode.isDevMode) return <devMode.DevPreview fromChildren={children} />;

    return children;
  }, [isLoading, error, overlay, children, devMode]);

  return (
    <Drawer
      classNames={classes}
      position="right"
      withCloseButton={false}
      onClose={onClose}
      title={
        <Flex.Row fullWidth justify="space-between" align="flex-start">
          <RpiTitle>{title || ""}</RpiTitle>
          <RpaButtonLink onClick={onClose}>
            <IconV3 icon="close" color={theme.other.semanticColors.textIconBold} />
          </RpaButtonLink>
        </Flex.Row>
      }
      {...props}
    >
      {!overlay && <Flex.Column sx={{ flex: 1, overflowY: "auto" }} children={childrenRender} />}
      {overlay && <RpaDrawerOverlay overlay={true} {...overlay} />}
    </Drawer>
  );
};

export interface RpaDrawerOverlayProps {
  type: "loading" | "success" | "error";
  message?: string;
  description?: string;
  overlay?: boolean;
}

export const RpaDrawerOverlay: React.FC<RpaDrawerOverlayProps> = ({ type, message, description, overlay = true }) => {
  const { colors } = useMantineTheme();

  const icon = React.useMemo(() => {
    switch (type) {
      case "loading":
        return <Loader color={colors.calm[5]} size={100} />;
      case "success":
        return <IconV2.Success color={colors.calm[5]} size={{ height: 100 }} />;
      case "error":
        return <IconV2.ErrorOutline color={colors.spark[5]} size={{ height: 100 }} />;
    }
  }, [type]);

  return (
    <Flex.Column
      justify="center"
      align="center"
      gap={8}
      px={16}
      py={32}
      sx={[
        (theme) => ({
          background: theme.colors.sky[5],
          width: "100%",
          height: "100%",
        }),
        optObj(overlay, {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }),
      ]}
    >
      <Flex mb={16} children={icon} />

      {message && (
        <RpiText
          type="p2"
          weight="regular"
          color={(theme) => theme.colors.brand[5]}
          sx={{ textAlign: "center" }}
          children={message}
        />
      )}

      {description && (
        <RpiText
          type="error"
          weight="regular"
          color={(theme) => theme.colors.brand[4]}
          sx={{ textAlign: "center" }}
          children={description}
        />
      )}
    </Flex.Column>
  );
};
